import { Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { shotBandThresholds } from './constants';
import { getPowerHudReadout } from './hudReadout';
import { BallGlyph, BurstGlyph, ResetGlyph, ReticleGlyph, StreakGlyph } from './hudGlyphs';
import { RenderQuality, UiState } from './types';

interface HudOverlayProps {
  uiState: UiState;
  visitBest: number;
  muted: boolean;
  quality: RenderQuality;
  onReset: () => void;
  onReplay: () => void;
  onToggleMuted: () => void;
}

interface PowerTrayProps {
  power: number;
  uiState: UiState;
}

const statItems = [
  {
    icon: BallGlyph,
    valueKey: 'goals',
    tone: 'text-slate-50',
    testId: 'penalty-run-stat-goals',
    label: 'Goals',
  },
  {
    icon: BurstGlyph,
    valueKey: 'perfectGoals',
    tone: 'text-emerald-200',
    testId: 'penalty-run-stat-perfects',
    label: 'Perfect',
  },
  {
    icon: StreakGlyph,
    valueKey: 'streak',
    tone: 'text-amber-200',
    testId: 'penalty-run-stat-streak',
    label: 'Streak',
  },
] as const;

const HudOverlay = ({ uiState, visitBest, muted, quality, onReset, onReplay, onToggleMuted }: HudOverlayProps) => {
  const useBackdropBlur = quality === 'full';

  return (
    <div className="pointer-events-none absolute inset-0 z-10">
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#04111de8] via-[#04111d80] to-transparent" />

      <div className="absolute left-3 top-[5.15rem] flex max-w-[12rem] flex-wrap gap-1.5 sm:left-5 sm:top-5 sm:max-w-none sm:flex-nowrap sm:gap-1.5">
        {statItems.map(({ icon: Icon, valueKey, tone, testId, label }) => (
          <div
            key={testId}
            aria-label={`${label} ${uiState.stats[valueKey]}`}
            className={cn(
              'flex min-w-[3.55rem] items-center gap-1.5 rounded-2xl border border-white/10 bg-[#08111d]/90 px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] sm:min-w-[4.35rem] sm:gap-1.5 sm:px-2 sm:py-2',
              useBackdropBlur && 'backdrop-blur-sm',
            )}
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/[0.07] sm:h-8 sm:w-8">
              <Icon className={cn('h-3.5 w-3.5 sm:h-4 sm:w-4', tone)} />
            </span>
            <div className="min-w-0">
              <div
                data-testid={testId}
                className="font-mono text-xs font-black tracking-[0.12em] text-white sm:text-sm sm:tracking-[0.2em]"
              >
                {String(uiState.stats[valueKey]).padStart(2, '0')}
              </div>
              <div className="text-[0.48rem] font-semibold uppercase tracking-[0.12em] text-slate-400 sm:text-[0.6rem] sm:tracking-[0.18em]">
                {label}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="pointer-events-auto absolute right-3 top-3 flex flex-col-reverse gap-2 sm:right-5 sm:top-5 sm:flex-row">
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onToggleMuted();
          }}
          aria-label={muted ? 'Turn game sound on' : 'Mute game sound'}
          aria-pressed={muted}
          className="flex min-h-11 min-w-11 items-center justify-center rounded-full border border-white/15 bg-[#08111d]/92 text-slate-100 transition duration-150 hover:-translate-y-0.5 hover:border-white/30 hover:bg-[#132033] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300"
        >
          {muted ? <VolumeX className="h-[1.05rem] w-[1.05rem]" /> : <Volume2 className="h-[1.05rem] w-[1.05rem]" />}
        </button>
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onReset();
          }}
          aria-label="Reset Penalty Run"
          className="flex min-h-11 min-w-11 items-center justify-center rounded-full border border-white/15 bg-[#08111d]/92 text-slate-100 transition duration-150 hover:-translate-y-0.5 hover:border-white/30 hover:bg-[#132033] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300"
        >
          <ResetGlyph className="h-[1.05rem] w-[1.05rem]" />
        </button>
      </div>

      <div className="absolute inset-x-16 top-3 flex justify-center sm:top-5 sm:px-24">
        <div
          className={cn(
            'w-full max-w-[14rem] rounded-[1.2rem] border border-white/10 bg-[#07111b]/92 px-3 py-2 text-center shadow-[0_18px_42px_rgba(2,6,23,0.24)] sm:max-w-[17rem] sm:px-4 sm:py-3',
            useBackdropBlur && 'backdrop-blur-sm',
          )}
        >
          <div
            className="mb-1.5 flex items-center justify-center gap-1.5"
            aria-label={`Shot ${uiState.shotNumber} of ${uiState.totalShots}`}
          >
            {Array.from({ length: uiState.totalShots }, (_, index) => (
              <span
                key={index}
                className={cn(
                  'h-1.5 rounded-full transition-[width,background-color] sm:h-2',
                  index < uiState.stats.attempts
                    ? 'w-5 bg-emerald-300 sm:w-7'
                    : index === uiState.stats.attempts && uiState.phase !== 'summary'
                      ? 'w-7 bg-white sm:w-9'
                      : 'w-3 bg-white/20 sm:w-4',
                )}
              />
            ))}
          </div>
          <p className="text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-slate-400">
            {uiState.phase === 'summary' ? 'Run complete' : `Shot ${uiState.shotNumber} of ${uiState.totalShots}`}
          </p>
          <p className={cn('mt-1 text-sm font-semibold tracking-[-0.01em] sm:text-base', uiState.toneClassName)}>{uiState.statusTitle}</p>
        </div>
      </div>

      {uiState.phase === 'summary' ? (
        <div className="pointer-events-auto absolute inset-0 flex items-center justify-center bg-[#020712]/72 px-5 py-24">
          <div
            data-testid="penalty-run-summary"
            role="status"
            className="w-full max-w-sm rounded-[1.75rem] border border-emerald-300/18 bg-[#07111b]/96 p-5 text-center shadow-[0_28px_70px_rgba(2,6,23,0.46)] sm:p-7"
          >
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-emerald-200">Five shots complete</p>
            <div className="mt-3 text-5xl font-black tracking-[-0.06em] text-white">{uiState.runSummary.score}</div>
            <p className="mt-1 text-sm text-slate-300">
              Grade <span className="font-bold text-emerald-200">{uiState.runSummary.grade}</span>
              {' · '}Best this visit{' '}
              <span data-testid="penalty-run-visit-best" className="font-bold text-white">
                {visitBest}
              </span>
            </p>
            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              <div>
                <strong className="block text-lg text-white">{uiState.runSummary.goals}</strong>
                <span className="text-[0.62rem] uppercase tracking-[0.14em] text-slate-400">Goals</span>
              </div>
              <div>
                <strong className="block text-lg text-white">{uiState.runSummary.perfectGoals}</strong>
                <span className="text-[0.62rem] uppercase tracking-[0.14em] text-slate-400">Perfect</span>
              </div>
              <div>
                <strong className="block text-lg text-white">{uiState.runSummary.bestStreak}</strong>
                <span className="text-[0.62rem] uppercase tracking-[0.14em] text-slate-400">Best streak</span>
              </div>
            </div>
            <button
              type="button"
              onClick={onReplay}
              className="mt-6 inline-flex min-h-12 w-full items-center justify-center rounded-full bg-emerald-300 px-5 py-3 text-sm font-black text-slate-950 transition duration-150 hover:-translate-y-0.5 hover:bg-emerald-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-emerald-200"
            >
              Play another run
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const PowerTray = ({ power, uiState }: PowerTrayProps) => {
  const { helperCopy, laneLabel, liveBand, statusLabel, statusTone } = getPowerHudReadout(power, uiState);
  const isAim = uiState.phase === 'aim';
  const markerLeft = `calc(${(power * 100).toFixed(2)}% - 0.375rem)`;
  const powerFillWidth = `${(power * 100).toFixed(2)}%`;
  const greenStart = `${shotBandThresholds.greenStart * 100}%`;
  const greenWidth = `${(shotBandThresholds.greenEnd - shotBandThresholds.greenStart) * 100}%`;
  const dangerStart = `${shotBandThresholds.redHighMin * 100}%`;

  return (
    <div
      data-testid="penalty-run-power-tray"
      data-phase={uiState.phase}
      className="pointer-events-none relative z-20 grid min-h-[5.5rem] grid-cols-[1fr_auto_1fr] items-center gap-2 border-t border-white/10 bg-[#06111c] px-3 py-2.5 sm:min-h-[5.75rem] sm:px-5"
    >
      {isAim ? (
        <>
          <div className="col-span-3 mb-0.5 flex items-center justify-between gap-2">
            <span className="text-[0.58rem] font-semibold uppercase tracking-[0.18em] text-slate-300 sm:text-[0.65rem]">Power</span>
            <div className="inline-flex items-center gap-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.14em] text-slate-200 sm:text-[0.65rem]">
              <ReticleGlyph className="h-3.5 w-3.5 shrink-0" />
              <span>{laneLabel} target</span>
            </div>
          </div>
          <div className="relative col-span-3 h-[1.1rem] overflow-hidden rounded-full border border-white/15 bg-[#04101b] sm:h-5">
            <div className="absolute inset-[1px] rounded-full bg-[linear-gradient(90deg,#43151d_0_14%,#5b3c16_14_54%,#0d5b47_54_63%,#5b3c16_63_88%,#43151d_88%)]" />
            <div className="absolute inset-y-[1px] left-0 rounded-full bg-white/18" style={{ width: powerFillWidth }} />
            <div
              className="absolute inset-y-[1px] rounded-full border border-emerald-100/60 bg-emerald-200/16"
              style={{ left: greenStart, width: greenWidth }}
            />
            <div
              className="absolute inset-y-[1px] rounded-r-full bg-gradient-to-r from-transparent to-rose-500/24"
              style={{ left: dangerStart, right: 0 }}
            />
            <div
              data-testid="penalty-run-timing-marker"
              className="absolute top-1/2 h-[1.125rem] w-3 -translate-y-1/2 rounded-[0.2rem] border border-slate-950/35 bg-white shadow-[0_0_12px_rgba(255,255,255,0.72)] sm:h-5"
              style={{ left: markerLeft }}
            >
              <span
                className={cn(
                  'absolute inset-[2px] rounded-[0.1rem]',
                  liveBand === 'green' ? 'bg-emerald-300' : liveBand === 'yellow' ? 'bg-amber-200' : 'bg-rose-300',
                )}
              />
            </div>
          </div>
          <span className="text-left text-[0.55rem] font-semibold uppercase tracking-[0.14em] text-rose-200 sm:text-[0.62rem]">Soft</span>
          <span
            className={cn(
              'rounded-full border px-2 py-1 text-[0.55rem] font-semibold uppercase tracking-[0.14em] sm:text-[0.62rem]',
              statusTone,
            )}
          >
            {statusLabel}
          </span>
          <span className="text-right text-[0.55rem] font-semibold uppercase tracking-[0.14em] text-rose-200 sm:text-[0.62rem]">Hard</span>
        </>
      ) : (
        <div className="col-span-3 flex items-center justify-center gap-2 text-center">
          <span
            className={cn(
              'rounded-full border px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.16em]',
              uiState.phase === 'result' ? statusTone : 'border-white/10 bg-white/[0.05] text-slate-200',
            )}
          >
            {uiState.phase === 'result' ? statusLabel : helperCopy}
          </span>
        </div>
      )}
    </div>
  );
};

export default HudOverlay;
